import { useState, useCallback } from 'react';

/**
 * useDialog Hook
 * Provides easy-to-use methods for showing dialogs
 */
export const useDialog = () => {
    const [dialogState, setDialogState] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'confirm',
        variant: 'default',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        placeholder: '',
        defaultValue: '',
        onConfirm: null,
        onCancel: null
    });

    const closeDialog = useCallback(() => {
        setDialogState(prev => ({ ...prev, isOpen: false }));
    }, []);

    const confirm = useCallback(({
        title = 'Confirm',
        message,
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        variant = 'default'
    }) => {
        return new Promise((resolve) => {
            setDialogState({
                isOpen: true,
                title,
                message,
                type: 'confirm',
                variant,
                confirmText,
                cancelText,
                placeholder: '',
                defaultValue: '',
                onConfirm: () => resolve(true),
                onCancel: () => resolve(false)
            });
        });
    }, []);

    const alert = useCallback(({
        title = 'Alert',
        message,
        confirmText = 'OK',
        variant = 'info'
    }) => {
        return new Promise((resolve) => {
            setDialogState({
                isOpen: true,
                title,
                message,
                type: 'alert',
                variant,
                confirmText,
                cancelText: '',
                placeholder: '',
                defaultValue: '',
                onConfirm: () => resolve(true),
                onCancel: () => resolve(false)
            });
        });
    }, []);

    const prompt = useCallback(({
        title = 'Input',
        message,
        placeholder = '',
        defaultValue = '',
        confirmText = 'Submit',
        cancelText = 'Cancel',
        variant = 'default'
    }) => {
        return new Promise((resolve) => {
            setDialogState({
                isOpen: true,
                title,
                message,
                type: 'prompt',
                variant,
                confirmText,
                cancelText,
                placeholder,
                defaultValue,
                onConfirm: (value) => resolve(value),
                onCancel: () => resolve(null)
            });
        });
    }, []);

    return {
        dialogState,
        closeDialog,
        confirm,
        alert,
        prompt
    };
};
